import React from 'react';

import { uniq } from 'lodash-es';

import Button from 'components/modules/button/Button';
import ErrorList from 'components/modules/forms/ErrorList';
import Field from 'components/modules/forms/Field';
import FormGroup from 'components/modules/forms/FormGroup';
import Input from 'components/modules/forms/Input';
import LabelWithStatus from 'components/modules/forms/LabelWithStatus';
import PasswordInput from 'components/modules/forms/PasswordInput';
import useReactHookForm from 'hooks/common/useReactHookForm';
import { AccountRegistrationFormValues } from 'interfaces/registration';

import CSSModule from './AccountRegistrationForm.module.scss';
import { validationSchema } from './form/constants';

interface Props {
  formValues: AccountRegistrationFormValues;
  onSubmit: (formValues: AccountRegistrationFormValues) => void;
  isPC: boolean;
}

const AccountRegistrationForm: React.FC<Props> = ({
  formValues,
  onSubmit,
  isPC,
}) => {
  const {
    registerWithInnerRef,
    handleSubmit,
    formState: {
      isSubmitting,
      isValid,
      touchedFields,
      validFields,
      errorMessages,
    },
    trigger,
  } = useReactHookForm<AccountRegistrationFormValues>({
    validationSchema,
    defaultValues: formValues,
  });

  const inputDisabled = isSubmitting;
  const buttonDisabled = !isValid || isSubmitting;

  const passwordErrors = uniq([
    ...(errorMessages.password || []),
    ...(errorMessages.password_confirmation || []),
  ]);

  const nameErrors = [
    ...(errorMessages.last_name || []),
    ...(errorMessages.first_name || []),
  ];

  const nameKanaErrors = [
    ...(errorMessages.last_name_kana || []),
    ...(errorMessages.first_name_kana || []),
  ];

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      className={CSSModule.AccountRegistrationForm}
    >
      <FormGroup>
        <LabelWithStatus required valid={validFields.code}>
          団体会員コード
        </LabelWithStatus>
        <Field>
          <Input
            type="text"
            {...registerWithInnerRef('code')}
            placeholder="団体会員コードを入力"
            disabled={inputDisabled}
          />
        </Field>
        <ErrorList errors={errorMessages.code || []} />
        <ul className={CSSModule.Comment}>
          <li>
            <p>
              学校から配布された団体会員コードを<b>半角数字</b>
              で入力してください。
            </p>
          </li>
          <li>
            <p>
              団体会員コードは１つにつき１アカウントまで登録可能です。複数のアカウントを登録することはできません。
            </p>
          </li>
        </ul>
      </FormGroup>
      <FormGroup>
        <LabelWithStatus
          required
          valid={validFields.first_name && validFields.last_name}
        >
          保護者氏名
        </LabelWithStatus>
        <div className={CSSModule.FieldGroup__TwoLines}>
          <Field className={CSSModule.Field}>
            <Input
              type="text"
              {...registerWithInnerRef('last_name')}
              disabled={inputDisabled}
              placeholder="例）山田"
            />
          </Field>
          <Field className={CSSModule.Field}>
            <Input
              type="text"
              {...registerWithInnerRef('first_name')}
              disabled={inputDisabled}
              placeholder="例）太郎"
            />
          </Field>
        </div>
        <ErrorList errors={nameErrors} />
      </FormGroup>
      <FormGroup>
        <LabelWithStatus
          required
          valid={validFields.first_name_kana && validFields.last_name_kana}
        >
          保護者氏名（かな）
        </LabelWithStatus>
        <div className={CSSModule.FieldGroup__TwoLines}>
          <Field className={CSSModule.Field}>
            <Input
              type="text"
              {...registerWithInnerRef('last_name_kana')}
              disabled={inputDisabled}
              placeholder="例）やまだ"
            />
          </Field>
          <Field className={CSSModule.Field}>
            <Input
              type="text"
              {...registerWithInnerRef('first_name_kana')}
              disabled={inputDisabled}
              placeholder="例）たろう"
            />
          </Field>
        </div>
        <ErrorList errors={nameKanaErrors} />
      </FormGroup>
      <FormGroup>
        <LabelWithStatus required valid={validFields.email}>
          メールアドレス
        </LabelWithStatus>
        <Field>
          <Input
            type="text"
            {...registerWithInnerRef('email')}
            disabled={inputDisabled}
            placeholder="メールアドレスを入力"
          />
          <ErrorList errors={errorMessages.email || []} />
          <ul className={CSSModule.Comment}>
            <li>
              <p>携帯会社のメールアドレスは受信できない場合があります。</p>
            </li>
            <li>
              <p>
                <b>PCのメールアドレスなどのご登録</b>を推奨します。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      <FormGroup>
        <LabelWithStatus
          required
          valid={validFields.password && validFields.password_confirmation}
        >
          パスワード
        </LabelWithStatus>
        <Field>
          <PasswordInput
            {...registerWithInnerRef('password', {
              onChange: () => {
                if (
                  touchedFields.password_confirmation ||
                  formValues.password_confirmation
                ) {
                  void trigger('password_confirmation');
                }
              },
            })}
            disabled={inputDisabled}
            autoComplete="new-password"
            placeholder="パスワードを入力"
          />
        </Field>
        <Field>
          <PasswordInput
            {...registerWithInnerRef('password_confirmation')}
            disabled={inputDisabled}
            autoComplete="new-password"
            placeholder="パスワードを入力（確認用）"
          />
        </Field>
        <ErrorList errors={passwordErrors} />
        <ul className={CSSModule.Comment}>
          <li className={CSSModule.Comment__Warning}>
            <p>最低6文字以上にしてください。</p>
          </li>
          <li className={CSSModule.Comment__Warning}>
            <p>
              半角アルファベットと半角数字をそれぞれ1文字以上入れてください。
            </p>
          </li>
          <li>
            <p>
              名前＋生年月日などは推測されやすいため、英数字以外の記号組み合わせを推奨します。
            </p>
          </li>
        </ul>
      </FormGroup>
      <div
        className={
          isPC ? CSSModule.SubmitButtonArea : CSSModule.SubmitButtonArea__Mobile
        }
      >
        <Button
          className={
            isPC ? CSSModule.SubmitButton : CSSModule.SubmitButton__Mobile
          }
          color="primary"
          type="submit"
          disabled={buttonDisabled}
        >
          確認画面へ
        </Button>
      </div>
    </form>
  );
};

export default AccountRegistrationForm;
